import { object, string, boolean, array, number, } from 'yup';
import { EmailTemplateTypes, mergeTags, EMAIL_SUBJECT_MAX_SAFE, } from '../../constants';
import { SignaturePositions } from '../../models';
export function extractMergeTagNames(content) {
    const tagRegex = /(?:{{{|{{)\s*([\w_]+)\s*(?:}}}|}})/g;
    const usedTags = new Set();
    let match;
    while ((match = tagRegex.exec(content)) !== null) {
        usedTags.add(match[1]);
    }
    return usedTags;
}
function getMergeTagName(rawValue) {
    const match = rawValue.match(/{{{\s*([\w_]+)\s*}}}/);
    if (match) {
        return match[1];
    }
    const normalized = rawValue.trim();
    return normalized.length ? normalized : null;
}
export function getAllowedMergeTagNamesForType(type, options = {}) {
    const { excludeArchived = false, excludeDevOnly = false, } = options;
    const allowedTagNames = new Set();
    const tagDefinitions = Object.values(mergeTags[type] || {});
    for (const def of tagDefinitions) {
        if (excludeArchived && def.archived) {
            continue;
        }
        if (excludeDevOnly && def.devOnly) {
            continue;
        }
        const tagName = getMergeTagName(def.value);
        if (tagName) {
            allowedTagNames.add(tagName);
        }
    }
    return allowedTagNames;
}
export function getInvalidMergeTagNamesForType(type, content, options = {}) {
    const allowedTagNames = getAllowedMergeTagNamesForType(type, options);
    const usedTags = extractMergeTagNames(content);
    const invalidTags = [];
    for (const usedTag of usedTags) {
        if (!allowedTagNames.has(usedTag)) {
            invalidTags.push(usedTag);
        }
    }
    return invalidTags;
}
export const emailTemplateSchema = object({
    logo_url: string().url(`Logo URL is invalid`).nullable(),
    type: string().oneOf([
        EmailTemplateTypes.LATE_FEE,
        EmailTemplateTypes.REMINDER,
        EmailTemplateTypes.DISCOUNT,
        EmailTemplateTypes.STATEMENT_EMAIL,
        EmailTemplateTypes.STATEMENT_INTEREST_EMAIL,
        EmailTemplateTypes.AFTER_MARKED_PAID,
        EmailTemplateTypes.QUOTE,
        EmailTemplateTypes.ANNOUNCEMENT,
        EmailTemplateTypes.PAYMENT_PLAN_REMINDER,
    ], 'Invalid Template Type').required(`Type is required`),
    main_html_content: string().required(`Email Content is required`),
    main_json_content: object().required(`Email Content is required`),
    include_upcoming_latefee: boolean().default(false).required(`Include upcoming late fee is required`),
    secondary_html_content: string().when('include_upcoming_latefee', {
        is: true,
        then: s => s.required(`Secondary email content is required when including upcoming late fee`),
        otherwise: s => s.nullable(),
    }),
    secondary_json_content: object().when('include_upcoming_latefee', {
        is: true,
        then: s => s.required(`Secondary email content is required when including upcoming late fee`),
        otherwise: s => s.nullable(),
    }),
    name: string().required(`Template name is required`),
    primary_color: string().required(`Primary color is required`),
    secondary_color: string().required(`Secondary color is required`),
    subject: string().max(EMAIL_SUBJECT_MAX_SAFE, `Subject must be ${EMAIL_SUBJECT_MAX_SAFE} characters or fewer`).required(`Subject is required`),
    attached_files: array().of(object({
        id: number().required(),
        filePath: string().required(),
        GCPBucketName: string().required(),
        mimeType: string().required(),
        size: number().required(),
        sizeFormatted: string().required(),
        uploadedAt: string().required(),
    })),
    signature_position: string().oneOf(Object.values(SignaturePositions)).nullable().optional(),
    include_payment_plan_section: boolean().default(false).nullable(),
    payment_plan_html_content: string().when('include_payment_plan_section', {
        is: true,
        then: s => s.required(`Payment plan section content is required when including payment plan section`),
        otherwise: s => s.nullable(),
    }),
    payment_plan_json_content: object().when('include_payment_plan_section', {
        is: true,
        then: s => s.required(`Payment plan section content is required when including payment plan section`),
        otherwise: s => s.nullable(),
    }),
})
    .test('invalid-merge-tags', 'HTML content contains invalid merge tags for its type', function (value) {
    const { type, main_html_content, secondary_html_content } = value;
    if (!type || !mergeTags[type]) {
        return true;
    }
    const allContent = (main_html_content || '') + (secondary_html_content || '');
    if (!allContent) {
        return true;
    }
    const invalidTags = getInvalidMergeTagNamesForType(type, allContent);
    if (invalidTags.length > 0) {
        return this.createError({
            message: `The following merge tags are not valid for this email type: ${invalidTags.join(', ')}`,
        });
    }
    return true;
});
//# sourceMappingURL=emailTemplate.js.map